import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import '../styles/HomePage.css';

const HomePage = () => {
  const { isAuthenticated } = useAuth();

  return (
    <>
      <SEO
        title="Home"
        description="Strat Guys - Your all-in-one gaming platform. Create tactical strategies, compete in Geoguesser challenges, and manage tournaments. Plan, play, and compete with precision."
        keywords="strat guys, gaming platform, strategy planner, geoguesser, tournament tools, tactical planning, game maps, competitive gaming, collaborative planning"
      />
      <div className="home-page">
        <div className="release-banner">
          <div className="release-left">
            <span className="badge sg-badge">BETA</span>
            <span>This application is in active beta. Features may change. Feedback is greatly appreciated!</span>
          </div>
          <div className="release-actions">
            <GiveFeedbackInline />
          </div>
        </div>

        <div className="contribute-banner">
          <div className="contribute-left">
            <div className="contribute-title">Contribute Geoguesser Levels</div>
            <div className="contribute-sub">
              Submit a new level for review. This is available to everyone (not just beta testers).
            </div>
          </div>
          <div className="contribute-actions">
            <SubmitLevelInline />
          </div>
        </div>

        {/* Hero Section */}
        <section className="hero">
          <div className="hero-content">
            <h1 className="hero-title">
              Your <span className="gradient-text">Gaming Platform</span>
            </h1>
            <p className="hero-subtitle">
              Strat Guys brings together everything you need: tactical strategy planning, competitive Geoguesser challenges, and tournament management. Plan, play, and compete all in one place.
            </p>
            <div className="hero-actions">
              {isAuthenticated ? (
                <>
                  <Link to="/maps" className="btn btn-primary">
                    Strategies
                  </Link>
                  <Link to="/geoguesser" className="btn btn-primary">
                    Geoguesser
                  </Link>
                  <Link to="/tournament-tools" className="btn btn-primary">
                    Tournaments
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/signup" className="btn btn-primary">
                    Sign Up Free
                  </Link>
                  <Link to="/login" className="btn btn-secondary">
                    Login
                  </Link>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Main Features - Three Equal Sections */}
        <section className="main-features">
          <div className="main-feature-card card">
            <div className="main-feature-icon" aria-hidden>S</div>
            <h2 className="main-feature-title">Strategy Planning</h2>
            <p className="main-feature-description">
              Create detailed tactical plans on real game maps. Draw paths, place operators, and coordinate with your team in real-time.
            </p>
            <div className="main-feature-highlights">
              <div className="highlight-item">
                <span>Precise drawing tools</span>
              </div>
              <div className="highlight-item">
                <span>Real-time collaboration</span>
              </div>
              <div className="highlight-item">
                <span>Save & organize strategies</span>
              </div>
            </div>
            <Link to="/maps" className="main-feature-cta">
              Explore Strategies →
            </Link>
          </div>

          <div className="main-feature-card card featured">
            <div className="main-feature-badge sg-badge">Popular</div>
            <div className="main-feature-icon" aria-hidden>G</div>
            <h2 className="main-feature-title">Geoguesser</h2>
            <p className="main-feature-description">
              Test your geography skills with custom maps and levels. Compete with friends, climb leaderboards, and submit your own challenges.
            </p>
            <div className="main-feature-highlights">
              <div className="highlight-item">
                <span>Custom maps & levels</span>
              </div>
              <div className="highlight-item">
                <span>Leaderboards & rankings</span>
              </div>
              <div className="highlight-item">
                <span>Submit your own levels</span>
              </div>
            </div>
            <Link to="/geoguesser" className="main-feature-cta">
              Play Geoguesser →
            </Link>
          </div>

          <div className="main-feature-card card">
            <div className="main-feature-icon" aria-hidden>T</div>
            <h2 className="main-feature-title">Tournament Tools</h2>
            <p className="main-feature-description">
              Organize and manage competitive tournaments. Create brackets, track scores, and run smooth competitions with professional tools.
            </p>
            <div className="main-feature-highlights">
              <div className="highlight-item">
                <span>Bracket management</span>
              </div>
              <div className="highlight-item">
                <span>Score tracking</span>
              </div>
              <div className="highlight-item">
                <span>Coming soon</span>
              </div>
            </div>
            <Link to="/tournament-tools" className="main-feature-cta">
              View Tournaments →
            </Link>
          </div>
        </section>

        {/* Use Cases Section - Replaces "How It Works" */}
        <section className="use-cases">
          <h2 className="section-title">What You Can Do</h2>
          <div className="use-cases-grid">
            <div className="use-case-card">
              <div className="use-case-header">
                <div className="use-case-icon">🎮</div>
                <h3>For Strategy Gamers</h3>
              </div>
              <p>Plan your next move with precision. Upload game maps, draw tactical paths, place operators, and collaborate with your team in real-time. Perfect for competitive gaming and team coordination.</p>
              <Link to="/maps" className="use-case-link">Start Planning →</Link>
            </div>
            <div className="use-case-card">
              <div className="use-case-header">
                <div className="use-case-icon">🌎</div>
                <h3>For Geoguesser Players</h3>
              </div>
              <p>Challenge yourself with custom maps and levels. Compete on leaderboards, track your progress, and submit your own challenges for the community. Test your geography knowledge and have fun.</p>
              <Link to="/geoguesser" className="use-case-link">Start Playing →</Link>
            </div>
            <div className="use-case-card">
              <div className="use-case-header">
                <div className="use-case-icon">🏅</div>
                <h3>For Tournament Organizers</h3>
              </div>
              <p>Run professional tournaments with ease. Manage brackets, track scores, and organize competitions for your community. Coming soon with powerful tournament management tools.</p>
              <Link to="/tournament-tools" className="use-case-link">Explore Tools →</Link>
            </div>
          </div>
        </section>

        {/* Platform Features - Refactored to showcase all three equally */}
        <section className="features">
          <h2 className="section-title">Platform Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon" aria-hidden>R</div>
              <h3>Real-time Collaboration</h3>
              <p>Work together instantly with WebSocket support. See changes in real-time whether you're planning strategies or competing in challenges.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon" aria-hidden>L</div>
              <h3>Leaderboards & Rankings</h3>
              <p>Track your progress across Geoguesser challenges. Compete with others and see where you rank on global and custom leaderboards.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon" aria-hidden>P</div>
              <h3>Precise Planning Tools</h3>
              <p>Professional drawing tools with operator placement, line drawing, and coordinate systems for detailed strategy planning.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon" aria-hidden>T</div>
              <h3>Team Collaboration</h3>
              <p>Invite teammates, share strategies, and coordinate in real-time. Perfect for competitive teams and gaming communities.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon" aria-hidden>C</div>
              <h3>Community Content</h3>
              <p>Submit your own Geoguesser levels, share strategies, and contribute to the growing Strat Guys community.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon" aria-hidden>O</div>
              <h3>Save & Organize</h3>
              <p>Save your strategies, organize by map, and access your content anytime. Everything is stored securely and synced across devices.</p>
            </div>
          </div>
        </section>

        {/* Final CTA - Simplified */}
        <section className="cta">
          <h2>Ready to Get Started?</h2>
          <p>Join Strat Guys today and experience the complete gaming platform.</p>
          {!isAuthenticated && (
            <Link to="/signup" className="btn btn-primary btn-large">
              Start Free Today
            </Link>
          )}
        </section>
      </div>
    </>
  );
};

export default HomePage;

function GiveFeedbackInline() {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button type="button" className="btn btn-secondary" style={{ marginLeft: '0.5rem' }} onClick={() => setOpen(true)}>
        Give Feedback
      </button>
      <React.Suspense fallback={null}>
        {open && <FeedbackModal isOpen={open} onClose={() => setOpen(false)} />}
      </React.Suspense>
    </>
  );
}

const FeedbackModal = React.lazy(() => import('../components/common/FeedbackModal'));
const LevelSubmissionModal = React.lazy(() => import('../components/common/LevelSubmissionModal'));

function SubmitLevelInline() {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={() => setOpen(true)}>
        Submit a Geoguesser Level
      </button>
      <React.Suspense fallback={null}>
        {open && <LevelSubmissionModal isOpen={open} onClose={() => setOpen(false)} />}
      </React.Suspense>
    </>
  );
}
